/**
 * useQuickOrient — lightweight first-run coach marks.
 *
 * Creator mode: 4 base steps (scene selector, comments/approval, shot lane, AI/audio gutter),
 * plus a 5th (hamburger menu / back to projects) appended in embedded creator mode only when
 * the scene-board shell is mounted — see backToScenesStep in TimelineView.tsx.
 * Reviewer mode: 3 steps (shot lane, comments/approval, viewer markup).
 *
 * SCRIPT TAB (owner, 20260904): a THIRD walk-through of the same shape, on the same CoachMark
 * component — five steps naming the Scenes / Characters / Locations sub-tabs, View changes and
 * the import button. It fires on the first entry to the Script tab (not on a first import, which
 * is what the retired first-import GuideRail flow waited for) and lives in
 * components/ScreenplayManager/ScriptOrient.tsx; only its seen-flag and its step BUILDER live
 * here, beside the other two so the three flags reset together.
 *
 * Fires before the Glow Up dialog. On completion, hands off to Glow Up (creator only).
 * Pressing "T" resets all first-run flags and replays the full sequence.
 */

import type { CoachStep } from '@/components/CoachMark.tsx';

const ORIENT_KEY = 'previs-seen-orient';
const ORIENT_REVIEW_KEY = 'previs-seen-orient-review';
const SCRIPT_ORIENT_KEY = 'previs-seen-orient-script';

// ── Persistence (localStorage + cookie dual-write, same as GlowUp) ──

function getFlag(key: string): boolean {
  try { if (localStorage.getItem(key)) return true; } catch { /* noop */ }
  return document.cookie.includes(key + '=1');
}

function setFlag(key: string): void {
  try { localStorage.setItem(key, '1'); } catch { /* noop */ }
  try { document.cookie = `${key}=1;max-age=315360000;path=/;SameSite=Lax`; } catch { /* noop */ }
}

function clearFlag(key: string): void {
  try { localStorage.removeItem(key); } catch { /* noop */ }
  try { document.cookie = `${key}=;max-age=0;path=/;SameSite=Lax`; } catch { /* noop */ }
}

export function hasSeenOrient(): boolean { return getFlag(ORIENT_KEY); }
export function hasSeenOrientReview(): boolean { return getFlag(ORIENT_REVIEW_KEY); }
export function hasSeenScriptOrient(): boolean { return getFlag(SCRIPT_ORIENT_KEY); }
export function markSeenOrient(): void { setFlag(ORIENT_KEY); }
export function markSeenOrientReview(): void { setFlag(ORIENT_REVIEW_KEY); }
export function markSeenScriptOrient(): void { setFlag(SCRIPT_ORIENT_KEY); }

/** Reset the first-run flags — for dev testing ("T", and the Projects menu's Reset Tutorial Flags
 *  (Dev) entry). QA press ONE key to replay first-run guidance, so this stays the single authoritative reset:
 *  every first-run flag the app has is cleared here rather than at resetOrient's call sites.
 *  As of 20260904 that is exactly the three orient flags — creator, reviewer and the Script tab's
 *  — since the retired first-import guide (and its separate seenFlag module) is gone. */
export function resetOrient(): void {
  clearFlag(ORIENT_KEY);
  clearFlag(ORIENT_REVIEW_KEY);
  clearFlag(SCRIPT_ORIENT_KEY);
}

// ── Auto-arm decision ──

/** Everything Transport.tsx's auto-arm effect reads, as plain values so the rule is unit-testable
 *  without mounting the transport bar. */
export interface OrientArmContext {
  hasProject: boolean;
  /** A tour (either script — selectAnyTourActive) owns first-run guidance while it runs. */
  tutorialActive: boolean;
  /** The open document is a TUTORIAL copy (tutorialStore.inTutorialProject, from the setSqlitePath
   *  payload's `tutorial_project`). Separate from `tutorialActive` on purpose: the tour ends with
   *  the user still standing in the tutorial project, and that is precisely when these marks used
   *  to fire — see orientAutoArmMode's header. */
  inTutorialProject: boolean;
  /** The open document is the bundled EXAMPLE project (tutorialStore.inExampleProject, from the
   *  setSqlitePath payload's `example_project`). 20261009 (owner): the Example sits under New user
   *  guides and is minted fresh on every open, like the tutorial copies, and "those tutorials are
   *  for orienting users on their first custom project" — so it is excluded on the same terms. */
  inExampleProject: boolean;
  /** The AI workspace is mounted INSIDE TimelineView and hides the timeline under it
   *  (TimelineView's hideTimeline): the scene selector, comment panel, shot lane and gutter the
   *  coach marks point at are not on screen. */
  aiWorkspaceOpen: boolean;
  readOnly: boolean;
  seenOrient: boolean;
  seenOrientReview: boolean;
}

/**
 * Which Quick Orient walk-through should auto-arm on Timeline entry, or null for none.
 *
 * 20260904 (owner, iPhone): the Board's "Open AI Studio" path (enterAiWorkspaceFromBoard) sets
 * workspaceRoute 'timeline' with the workspace already open, so TimelineView — and Transport with
 * it — mounts under a surface that covers every orient target, and the marks fired on top of the
 * workspace. The rule is that orient arms only when the Timeline is actually showing; the caller
 * re-runs it when aiWorkspaceOpen flips, so a workspace close that falls back to the Timeline
 * (no returnToBoard) still arms for that first real look, while a returnToBoard close unmounts
 * the view and leaves the seen-flag untouched for a later Timeline visit.
 *
 * 20260922 (owner): NEVER INSIDE A TUTORIAL PROJECT, tour running or not. "Can we make it such-that
 * the Prep tab and Timeline tab tutorials … don't show up from within any of the tutorial projects?
 * We've tweaked their timing to not collide with the tutorials themselves but it's just a bit much
 * to have it appear after following the other tutorial. Better to reserve it for when the user is
 * back in their own project as it serves as a reminder then." `tutorialActive` alone could not
 * deliver that: the tour ENDS (End tour, or the finale's Play your scene) with the user still in the
 * tutorial copy, so the marks fired the moment it did. This is the document's identity, so it
 * outlives the tour — and it is a DEFERRAL, not a consumption: the seen flag is untouched, so the
 * user's own next project still gets the marks, which is the whole point of the ruling.
 */
export function orientAutoArmMode(c: OrientArmContext): 'creator' | 'reviewer' | null {
  if (!c.hasProject) return null;
  if (c.tutorialActive) return null;
  if (c.inTutorialProject) return null;
  // 20261009 (owner): nor inside the bundled Example project — a guide project, not the user's own.
  // A deferral like the term above: the seen flags are untouched.
  if (c.inExampleProject) return null;
  if (c.aiWorkspaceOpen) return null;
  if (c.readOnly) return c.seenOrientReview ? null : 'reviewer';
  return c.seenOrient ? null : 'creator';
}

// ── Step definitions ──

export const CREATOR_STEPS: CoachStep[] = [
  {
    target: 'scene-selector',
    title: 'Scenes & Script',
    text: 'Switch scenes and open your screenplay from here.',
    arrow: 'below',
  },
  {
    target: 'comments-approval',
    title: 'Comments & Approval',
    text: 'View shot comments and set approval status for your team.',
    arrow: 'below',
  },
  {
    target: 'shot-lane',
    title: 'Your Timeline',
    text: 'Drag edges to change timing. Click between shots to add transitions.',
    arrow: 'above',
  },
  {
    target: 'gutter-buttons',
    title: 'AI & Audio',
    text: 'AI image and video generation, text-to-speech, and audio recording tools.',
    arrow: 'right',
  },
];

// Embedded-mode-only step appended to CREATOR_STEPS in TimelineView when
// capabilities.bridge && capabilities.sceneBoardShell. Standalone web has no
// equivalent chrome to point at, so we don't show it there. TimelineView tracks
// whether the shell's Home button is on screen and calls buildBackToProjectsStep
// below so the title/text can be localized via the help namespace. Previously
// targeted a phantom div standing in for a Unity "Back to Projects" chrome
// button; that button was removed 2026-08-09, and the phantom was retired
// 2026-08-11 once the real hamburger became directly targetable (it's inside the
// WebView, unlike the old Unity button).
// Owner ask 2026-09-03: since the shell grew its own Home button
// (data-orient="projects-home" on SceneBoardPage.tsx, restored 2026-08-20),
// point the step at that directly rather than sending the user through the
// hamburger's Close Project item -- except on bars too narrow to hold it,
// where the hamburger fallback still applies.

// WHICH TARGET IS PICKED is TimelineView's `useUiStore(s => s.shellHomeShed)` — a flag
// SceneBoardPage publishes from a measurement of its own bar (SceneBoard/shellBarFit.ts).
// A PROJECTS_HOME_HIDDEN_QUERY constant used to live here and had to be kept byte-identical to a
// `@media (max-width: 600px)` block in SceneBoardPage.module.css; both are gone as of 20260904,
// and there is no breakpoint left for anything in this file to mirror.

/**
 * Pure builder for Quick Orient's fifth step, so it can be unit-tested without mounting
 * TimelineView. `homeHidden` is the caller's uiStore.shellHomeShed: false (Home button visible)
 * targets it directly with a left arrow; true (the bar shed it) falls back to the hamburger menu's
 * Close Project item, with the copy this step used before the Home button existed.
 */
export function buildBackToProjectsStep(homeHidden: boolean, tHelp: (key: string) => string): CoachStep {
  return homeHidden
    ? {
        target: 'hamburger-menu',
        title: tHelp('coachBackToProjectsTitle'),
        text: tHelp('coachBackToProjectsText'),
        arrow: 'right',
      }
    : {
        target: 'projects-home',
        title: tHelp('coachBackToProjectsTitle'),
        text: tHelp('coachBackToProjectsHomeText'),
        arrow: 'left',
      };
}

/** The five `data-orient` slugs the Script tab's coach marks point at, in step order. Exported so
 *  a test can assert the walk-through's targets without restating the strings, and so the three
 *  BrowseMode tab buttons + the two ScreenplayManager header buttons have one list to match. */
export const SCRIPT_ORIENT_TARGETS = [
  'script-tab-scenes',
  'script-tab-characters',
  'script-tab-locations',
  'script-view-changes',
  'script-import-revision',
] as const;

/** The Script tab's five coach marks, localized at build time (unlike CREATOR_STEPS/REVIEWER_STEPS
 *  above, whose copy is still hardcoded English literals). Steps 4 and 5 are TITLED WITH THE
 *  BUTTON'S OWN LABEL, passed in by the host from the same i18n key the button renders, so the
 *  title cannot disagree with the button in any language (the owner's 20260904 CTA rule — see
 *  Tutorial/CtaLine.tsx).
 *
 *  `arrow` is set on every step for completeness; CoachMark's geometry does not read it (the
 *  arrow always runs from the dialog's nearest corner to the target's nearest edge). All five
 *  targets sit above the centred dialog, hence 'above'. */
export function buildScriptOrientSteps(
  tHelp: (key: string) => string,
  labels: { viewChanges: string; importRevision: string },
): CoachStep[] {
  return [
    {
      target: 'script-tab-scenes',
      title: tHelp('coachScriptScenesTitle'),
      text: tHelp('coachScriptScenesText'),
      arrow: 'above',
    },
    {
      target: 'script-tab-characters',
      title: tHelp('coachScriptCharactersTitle'),
      text: tHelp('coachScriptCharactersText'),
      arrow: 'above',
    },
    {
      target: 'script-tab-locations',
      title: tHelp('coachScriptLocationsTitle'),
      text: tHelp('coachScriptLocationsText'),
      arrow: 'above',
    },
    {
      target: 'script-view-changes',
      title: labels.viewChanges,
      text: tHelp('coachScriptViewChangesText'),
      arrow: 'above',
    },
    {
      target: 'script-import-revision',
      title: labels.importRevision,
      text: tHelp('coachScriptImportRevisionText'),
      arrow: 'above',
    },
  ];
}

export const REVIEWER_STEPS: CoachStep[] = [
  {
    target: 'shot-lane',
    title: 'Shots',
    text: 'All shots in this scene. Click to select, use arrow keys to navigate.',
    arrow: 'above',
  },
  {
    target: 'comments-approval',
    title: 'Comments & Approval',
    text: 'Leave comments and set approval status for each shot.',
    arrow: 'below',
  },
  {
    target: 'viewer-markup',
    title: 'Visual Notes',
    text: 'Draw directly on shots to give visual feedback.',
    arrow: 'above',
  },
];
